import { useState } from "react";
import {
  GitCompare,
  ArrowRight,
  Plus,
  Minus,
  RefreshCw,
  Undo2,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PppcActivityEntry } from "@/services/pppcActivityLog";
import { diffPppcSnapshots, PppcChange } from "@/utils/pppcDiff";

interface PppcCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The newer entry (after). */
  current: PppcActivityEntry | null;
  /** The previous entry (before). */
  previous: PppcActivityEntry | null;
  /** Called when the user confirms reverting to the `previous` snapshot. */
  onRevert?: (previous: PppcActivityEntry) => Promise<void> | void;
  /** True while the revert mutation is running. */
  isReverting?: boolean;
}

const formatTs = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const CHANGE_META: Record<
  PppcChange,
  { label: string; tone: string; icon: typeof Plus }
> = {
  added: { label: "Added", tone: "bg-success-muted text-success", icon: Plus },
  removed: {
    label: "Removed",
    tone: "bg-destructive/10 text-destructive",
    icon: Minus,
  },
  changed: { label: "Changed", tone: "bg-primary/10 text-primary", icon: RefreshCw },
};

const choiceBadge = (v: "allow" | "deny" | "unset") => {
  if (v === "allow") return <Badge variant="default">Allow</Badge>;
  if (v === "deny") return <Badge variant="destructive">Deny</Badge>;
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Unset
    </Badge>
  );
};

export function PppcCompareDialog({
  open,
  onOpenChange,
  current,
  previous,
  onRevert,
  isReverting = false,
}: PppcCompareDialogProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canDiff = !!current?.snapshot && !!previous?.snapshot;
  const diff =
    canDiff && current?.snapshot && previous?.snapshot
      ? diffPppcSnapshots(previous.snapshot, current.snapshot)
      : null;

  const canRevert = !!onRevert && !!previous?.snapshot && !diff?.unchanged;

  const handleConfirmRevert = async () => {
    if (!previous || !onRevert) return;
    try {
      await onRevert(previous);
      setConfirmOpen(false);
      onOpenChange(false);
    } catch {
      // toast handled upstream
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-4 w-4" />
              Compare with previous
            </DialogTitle>
            <DialogDescription>
              {previous && current ? (
                <span className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono">{formatTs(previous.at)}</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="font-mono">{formatTs(current.at)}</span>
                </span>
              ) : (
                "Showing differences between the last two updates."
              )}
            </DialogDescription>
          </DialogHeader>

          {!canDiff ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Not enough history to compare. Make at least two updates to see a diff.
            </div>
          ) : diff?.unchanged ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No differences detected between these two snapshots.
            </div>
          ) : (
            <div className="space-y-5">
              {diff!.fieldChanges.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Policy fields
                  </h3>
                  <div className="space-y-3">
                    {diff!.fieldChanges.map((c) => (
                      <div key={c.field} className="border rounded-md p-3">
                        <p className="text-sm font-medium mb-2">{c.label}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-muted-foreground mb-1">Before</p>
                            <pre className="font-mono bg-muted rounded p-2 whitespace-pre-wrap break-all">
                              {c.before || "—"}
                            </pre>
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-1">After</p>
                            <pre className="font-mono bg-primary/5 border border-primary/20 rounded p-2 whitespace-pre-wrap break-all">
                              {c.after || "—"}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {diff!.serviceChanges.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Service changes ({diff!.serviceChanges.length})
                  </h3>
                  <div className="border rounded-md divide-y">
                    {diff!.serviceChanges.map((s) => {
                      const meta = CHANGE_META[s.change];
                      const Icon = meta.icon;
                      return (
                        <div
                          key={s.key}
                          className="flex items-center justify-between gap-3 py-2 px-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-full ${meta.tone} shrink-0`}
                            >
                              <Icon className="h-3 w-3" />
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {s.label}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {s.groupLabel}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {choiceBadge(s.before)}
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            {choiceBadge(s.after)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isReverting}
            >
              Close
            </Button>
            {canRevert && (
              <Button
                variant="default"
                onClick={() => setConfirmOpen(true)}
                disabled={isReverting}
              >
                {isReverting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Undo2 className="h-4 w-4 mr-2" />
                )}
                Revert to previous snapshot
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to previous snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              This will overwrite the current PPPC policy with the snapshot from{" "}
              <strong>{previous ? formatTs(previous.at) : ""}</strong>. The current
              state stays in the activity log, and the revert itself is recorded as a
              new entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReverting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmRevert();
              }}
              disabled={isReverting}
            >
              {isReverting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Revert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
